import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  #stagingObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#relocateSubMegaPromos();
    this.#observeStagingArea();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#stagingObserver?.disconnect();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed.
   * If a child/grandchild layer is open, go back one layer; otherwise close the main drawer.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#isLayerVisible('grandchildren')) {
      this.#hideLayer('grandchildren');
      return;
    }
    if (this.#isLayerVisible('children')) {
      this.#hideLayer('children');
      return;
    }

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer. When opening, show the root layer and ensure child layers are hidden.
   */
  toggle() {
    if (this.isOpen) {
      this.close();
      return;
    }
    const rootDrawer = this.#getDrawerByLayer('root');
    const childrenDrawer = this.#getDrawerByLayer('children');
    const grandchildrenDrawer = this.#getDrawerByLayer('grandchildren');
    if (rootDrawer) rootDrawer.classList.add('is-visible');
    if (childrenDrawer) {
      childrenDrawer.classList.remove('is-visible');
      childrenDrawer.setAttribute('aria-hidden', 'true');
    }
    if (grandchildrenDrawer) {
      grandchildrenDrawer.classList.remove('is-visible');
      grandchildrenDrawer.setAttribute('aria-hidden', 'true');
    }
    this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer (e.g. nested localization details).
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      details.closest('.start-level')?.classList.add('open');

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Open the children drawer for the given parent (called from root when clicking a parent link).
   * Bound as "open-children" in the template.
   * @param {Event} [event]
   */
  ['open-children'](event) {
    const button = event?.target?.closest?.('[data-open-children]');
    if (!button) return;

    const parentHandle = button.getAttribute('data-open-children');
    const childrenDrawer = this.#getDrawerByLayer('children');
    if (!childrenDrawer) return;

    this.#showChildrenDrawer(parentHandle);
    const panel = childrenDrawer.querySelector(`[data-parent="${parentHandle}"]`);
    if (panel) {
      panel.hidden = false;
      this.#whenDrawerOpenTransitionEnd(childrenDrawer, () => trapFocus(childrenDrawer));
    }
  }

  /**
   * Open the grandchildren drawer for the given parent and child.
   * Bound as "open-grandchildren" in the template.
   * @param {Event} [event]
   */
  ['open-grandchildren'](event) {
    const button = event?.target?.closest?.('[data-open-grandchildren]');
    if (!button) return;

    const parentHandle = button.getAttribute('data-open-grandchildren');
    const childHandle = button.getAttribute('data-child-handle');
    const grandchildrenDrawer = this.#getDrawerByLayer('grandchildren');
    if (!grandchildrenDrawer || !parentHandle || !childHandle) return;

    this.#showGrandchildrenDrawer(parentHandle, childHandle);
    const panel = grandchildrenDrawer.querySelector(
      `[data-parent="${parentHandle}"][data-child="${childHandle}"]`
    );
    if (panel) {
      panel.hidden = false;
      this.#whenDrawerOpenTransitionEnd(grandchildrenDrawer, () =>
        trapFocus(grandchildrenDrawer)
      );
    }
  }

  /**
   * Go back one layer (from children to root, or from grandchildren to children).
   * Bound as "back-layer" in the template.
   * @param {Event} [event]
   */
  ['back-layer'](event) {
    const button = event?.target?.closest?.('[data-drawer-back]');
    const target = button?.getAttribute('data-drawer-back');
    if (target === 'root') {
      this.#hideLayer('children');
    } else if (target === 'children') {
      this.#hideLayer('grandchildren');
    }
  }

  /**
   * Go back or close the main menu drawer (used by nested details e.g. localization).
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer and reset all layers.
   */
  close() {
    this.#hideLayer('grandchildren');
    this.#hideLayer('children');
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open.
   * When closing the main details, also reset children/grandchildren layers.
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    details.closest('.start-level')?.classList.remove('open');

    if (details === this.refs.details) {
      this.#resetLayers();
    }

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * @param {string} layer - 'children' | 'grandchildren'
   * @returns {HTMLElement | null}
   */
  #getDrawerByLayer(layer) {
    return this.querySelector(`[data-drawer-layer="${layer}"]`);
  }

  /**
   * @param {string} layer - 'children' | 'grandchildren'
   * @returns {boolean}
   */
  #isLayerVisible(layer) {
    const drawer = this.#getDrawerByLayer(layer);
    return drawer?.classList.contains('is-visible') ?? false;
  }

  #whenDrawerOpenTransitionEnd(drawer, callback) {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      drawer.removeEventListener('transitionend', onTransitionEnd);
      if (timeoutId !== null) clearTimeout(timeoutId);
      callback();
    };
    const onTransitionEnd = (event) => {
      if (event.propertyName === 'transform') run();
    };
    let timeoutId = setTimeout(run, 500);

    onAnimationEnd(drawer, run, { subtree: false });
    drawer.addEventListener('transitionend', onTransitionEnd, { once: false });
  }

  /**
   * Show the children drawer on top of root; root stays visible underneath.
   * @param {string} parentHandle
   */
  #showChildrenDrawer(parentHandle) {
    const rootDrawer = this.#getDrawerByLayer('root');
    const childrenDrawer = this.#getDrawerByLayer('children');
    if (!rootDrawer || !childrenDrawer) return;

    childrenDrawer.classList.remove('menu-drawer--closing');
    childrenDrawer.setAttribute('data-active-parent', parentHandle);
    childrenDrawer.querySelectorAll('.menu-drawer__layer-panel').forEach((panel) => {
      panel.hidden = panel.getAttribute('data-parent') !== parentHandle;
    });
    rootDrawer.setAttribute('aria-hidden', 'true');
    childrenDrawer.classList.add('is-visible');
    childrenDrawer.setAttribute('aria-hidden', 'false');
  }

  /**
   * Show the grandchildren drawer on top of children; children stays visible underneath.
   * @param {string} parentHandle
   * @param {string} childHandle
   */
  #showGrandchildrenDrawer(parentHandle, childHandle) {
    const childrenDrawer = this.#getDrawerByLayer('children');
    const grandchildrenDrawer = this.#getDrawerByLayer('grandchildren');
    if (!childrenDrawer || !grandchildrenDrawer) return;

    grandchildrenDrawer.classList.remove('menu-drawer--closing');
    grandchildrenDrawer.setAttribute('data-active-parent', parentHandle);
    grandchildrenDrawer.setAttribute('data-active-child', childHandle);
    grandchildrenDrawer.querySelectorAll('.menu-drawer__layer-panel').forEach((panel) => {
      panel.hidden =
        panel.getAttribute('data-parent') !== parentHandle ||
        panel.getAttribute('data-child') !== childHandle;
    });
    childrenDrawer.setAttribute('aria-hidden', 'true');
    grandchildrenDrawer.classList.add('is-visible');
    grandchildrenDrawer.setAttribute('aria-hidden', 'false');
  }

  /**
   * Synchronously reset layer state (used when closing main details). Does not trap focus.
   */
  #resetLayers() {
    const childrenDrawer = this.#getDrawerByLayer('children');
    const grandchildrenDrawer = this.#getDrawerByLayer('grandchildren');
    const rootDrawer = this.#getDrawerByLayer('root');
    if (childrenDrawer) {
      childrenDrawer.classList.remove('menu-drawer--closing', 'is-visible');
      childrenDrawer.setAttribute('aria-hidden', 'true');
      childrenDrawer.removeAttribute('data-active-parent');
      childrenDrawer.querySelectorAll('.menu-drawer__layer-panel').forEach((p) => (p.hidden = true));
    }
    if (grandchildrenDrawer) {
      grandchildrenDrawer.classList.remove('menu-drawer--closing', 'is-visible');
      grandchildrenDrawer.setAttribute('aria-hidden', 'true');
      grandchildrenDrawer.removeAttribute('data-active-parent');
      grandchildrenDrawer.removeAttribute('data-active-child');
      grandchildrenDrawer.querySelectorAll('.menu-drawer__layer-panel').forEach((p) => (p.hidden = true));
    }
    if (rootDrawer) {
      rootDrawer.classList.add('is-visible');
      rootDrawer.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Hide a layer and show the previous one; animate closing drawer out to the right, then trap focus.
   * Uses transitionend with a timeout fallback so finishHide always runs (e.g. when reduced motion).
   * @param {string} layer - 'children' | 'grandchildren'
   */
  #hideLayer(layer) {
    const drawer = this.#getDrawerByLayer(layer);
    if (!drawer) return;

    let finished = false;
    let fallbackTimeoutId = null;

    const finishHide = () => {
      if (finished) return;
      finished = true;
      if (fallbackTimeoutId !== null) {
        clearTimeout(fallbackTimeoutId);
        fallbackTimeoutId = null;
      }
      drawer.removeEventListener('transitionend', onTransitionEnd);
      drawer.classList.remove('menu-drawer--closing');
      drawer.classList.remove('is-visible');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.removeAttribute('data-active-parent');
      drawer.removeAttribute('data-active-child');
      drawer.querySelectorAll('.menu-drawer__layer-panel').forEach((panel) => {
        panel.hidden = true;
      });
      if (layer === 'grandchildren') {
        const childrenDrawer = this.#getDrawerByLayer('children');
        if (childrenDrawer) trapFocus(childrenDrawer);
      } else {
        const rootDrawer = this.#getDrawerByLayer('root');
        if (rootDrawer) trapFocus(rootDrawer);
      }
    };

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'transform') return;
      finishHide();
    };

    drawer.addEventListener('transitionend', onTransitionEnd, { once: false });

    const durationMs =
      Number.parseFloat(
        String(getComputedStyle(drawer).getPropertyValue('--drawer-animation-speed')).replace(/s$/, '') || '0.2',
        10
      ) * 1000;
    const fallbackDelay = Math.min(Math.max(durationMs + 100, 400), 600);
    fallbackTimeoutId = setTimeout(finishHide, fallbackDelay);

    drawer.classList.add('menu-drawer--closing');

    if (layer === 'grandchildren') {
      const childrenDrawer = this.#getDrawerByLayer('children');
      if (childrenDrawer) {
        childrenDrawer.classList.add('is-visible');
        childrenDrawer.setAttribute('aria-hidden', 'false');
      }
    } else {
      const rootDrawer = this.#getDrawerByLayer('root');
      if (rootDrawer) {
        rootDrawer.classList.add('is-visible');
        rootDrawer.setAttribute('aria-hidden', 'false');
      }
    }
  }

  /**
   * Clone sub-mega-sidebar blocks as mobile promo cards into matching grandchildren panels.
   */
  #relocateSubMegaPromos() {
    const section = this.closest('.shopify-section') || document;
    const sidebars = section.querySelectorAll('.sub-mega-sidebar--pending, .sub-mega-sidebar');
    const grandchildrenLayer = this.querySelector('[data-drawer-layer="grandchildren"]');
    if (!grandchildrenLayer) return;

    sidebars.forEach((sidebar) => {
      const parentHandle = sidebar.getAttribute('data-target-parent');
      const childHandle = sidebar.getAttribute('data-target-text');
      const panel = grandchildrenLayer.querySelector(
        `.menu-drawer__layer-panel[data-parent-handle="${parentHandle}"][data-child-handle="${childHandle}"]`
      );
      if (!panel) return;

      // Avoid duplicates
      if (panel.querySelector('.sub-mega-promo-card')) return;

      const promo = document.createElement('div');
      promo.className = 'sub-mega-promo-card';

      const links = sidebar.querySelector('.sub-mega-sidebar__links');
      if (links) {
        const mobileLinks = document.createElement('ul');
        mobileLinks.className = 'sub-mega-promo-card__links list-unstyled';
        links.querySelectorAll('.sub-mega-sidebar__link').forEach((link) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = link.href;
          a.className = 'sub-mega-promo-card__link';
          a.textContent = link.textContent.trim();
          li.appendChild(a);
          mobileLinks.appendChild(li);
        });
        promo.appendChild(mobileLinks);
      }

      const ctaImage = sidebar.querySelector('.sub-mega-sidebar__cta-image');
      if (ctaImage) {
        const wrapper = document.createElement('div');
        wrapper.className = 'sub-mega-promo-card__image-wrapper';
        const ctaLink = sidebar.querySelector('.sub-mega-sidebar__cta-link');
        if (ctaLink) {
          const a = document.createElement('a');
          a.href = ctaLink.href;
          a.appendChild(ctaImage.cloneNode(true));
          a.querySelector('img').className = 'sub-mega-promo-card__image';
          wrapper.appendChild(a);
        } else {
          const img = ctaImage.cloneNode(true);
          img.className = 'sub-mega-promo-card__image';
          wrapper.appendChild(img);
        }
        promo.appendChild(wrapper);
      }

      const ctaText = sidebar.querySelector('.sub-mega-sidebar__text');
      if (ctaText) {
        const p = document.createElement('p');
        p.className = 'sub-mega-promo-card__text';
        p.textContent = ctaText.textContent.trim();
        promo.appendChild(p);
      }

      panel.appendChild(promo);
    });
  }

  /**
   * Watch the staging container for new sub-mega blocks (handles section re-renders).
   */
  #observeStagingArea() {
    const section = this.closest('.shopify-section') || document;
    const staging = section.querySelector('.sub-mega-staging');
    if (!staging) return;

    this.#stagingObserver = new MutationObserver(() => {
      this.#relocateSubMegaPromos();
    });
    this.#stagingObserver.observe(staging, { childList: true, subtree: true });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
